import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../App';
import LoadingSpinner from '@shared/ui/LoadingSpinner';

/**
 * Civic interests, chosen from the legislative subject taxonomy.
 *
 * Interests used to be a free-text "comma-separated" box, so no two people
 * wrote the same interest the same way and nothing could ever match on it.
 * The `subjects` table is the interim taxonomy (decision 2026-08-03) — the
 * same list bills are categorised by, so an interest can eventually drive
 * relevance without a mapping step.
 *
 * The labels are legislature-facing today ("Capital Construction",
 * "Probate, Trusts, & Fiduciaries"); issue #1147 covers voter-friendly names.
 * When that lands, render the public name here and keep storing the value.
 *
 * Storage stays a comma-separated string, unchanged from the free-text field,
 * so nothing needs migrating: whatever a member already typed that matches a
 * subject simply appears selected.
 */
export interface InterestsPickerProps {
  /** Comma-separated subject names, as stored on the profile. */
  value: string;
  onChange: (value: string) => void;
}

/** Not a topic anyone chooses — it is the source data's "uncategorised" row. */
const EXCLUDED_SUBJECTS = new Set(['N/A']);

const splitInterests = (value: string): string[] =>
  value.split(',').map((s) => s.trim()).filter(Boolean);

const InterestsPicker: React.FC<InterestsPickerProps> = ({ value, onChange }) => {
  const { data: subjects = [], isLoading } = useQuery({
    // Already served for the legislation filters, so no new endpoint and no
    // deploy dependency; cached long because the taxonomy rarely changes.
    queryKey: ['bill-filter-options', 'subjects'],
    queryFn: async () => {
      const res = await api.get('/bills/filters/options');
      const list = res.data?.data?.subjects ?? res.data?.subjects ?? [];
      return (list as unknown[])
        .map((s) => (typeof s === 'string' ? s : (s as { name?: string })?.name))
        .filter((s): s is string => typeof s === 'string' && s.length > 0 && !EXCLUDED_SUBJECTS.has(s));
    },
    staleTime: 30 * 60 * 1000,
  });

  const selected = useMemo(() => new Set(splitInterests(value)), [value]);

  const toggle = (subject: string) => {
    const next = new Set(selected);
    if (next.has(subject)) next.delete(subject);
    else next.add(subject);
    // Preserve the order the subjects are listed in, so the stored string is
    // stable rather than ordered by whichever chip was clicked first.
    onChange(subjects.filter((s) => next.has(s)).join(', '));
  };

  if (isLoading) {
    return <LoadingSpinner size="sm" label="Loading interests" />;
  }

  if (subjects.length === 0) {
    return (
      <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
        Interest options are unavailable right now.
      </p>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Civic interests">
        {subjects.map((subject) => {
          const isSelected = selected.has(subject);
          return (
            <button
              key={subject}
              type="button"
              aria-pressed={isSelected}
              onClick={() => toggle(subject)}
              className={`px-3 py-1.5 rounded-full text-sm transition-colors border ${
                isSelected
                  ? 'bg-primary-100 text-primary-700 border-primary-300 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-700 font-medium'
                  : 'bg-background-tertiary text-text-secondary border-transparent hover:text-text-primary dark:bg-background-dark-tertiary dark:text-text-dark-secondary dark:hover:text-text-dark-primary'
              }`}
            >
              {subject}
            </button>
          );
        })}
      </div>
      <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
        Choose the policy areas you follow. {selected.size > 0 && `${selected.size} selected.`}
      </p>
    </div>
  );
};

export default InterestsPicker;
